import ConceptSwitcher from "@/components/ConceptSwitcher";
import BrandMark, { TAGLINE } from "@/components/BrandMark";
import { PHOTOS } from "@/constants/photos";
import {
  ArrowUpRight,
  ArrowRight,
  Compass,
  ChalkboardTeacher,
  Users,
  ShieldCheck,
  Microphone,
  Scales,
  Heart,
  Buildings,
  GraduationCap,
} from "@phosphor-icons/react";

// Concept 01 — Executive Assurance palette
const C = {
  bg: "#F6F3EE", // Warm Ivory
  surface: "#ECE7E0", // Soft Stone
  text: "#1F2326", // Ink Charcoal
  brand: "#1F5C63", // Deep Heritage Teal
  rose: "#C48A8C", // Muted Rose
  muted: "#6B7378", // Slate Gray
  line: "#D8D1C8", // Fog Line
  inverse: "#173C43", // Midnight Teal
  ivory: "#FAF7F2", // Soft Ivory
};

const navLinks = ["Who We Help", "Services", "Speaking", "About", "Insights", "Contact"];

const trustMarkers = [
  {
    Icon: Scales,
    label: "Employment-law fluent",
    detail:
      "Decisions framed with the legal context boards and executives actually need.",
  },
  {
    Icon: Buildings,
    label: "Senior HR practice",
    detail:
      "Years of operating-level HR judgment across mission-driven organizations.",
  },
  {
    Icon: GraduationCap,
    label: "Certified executive coaching",
    detail:
      "Formal coaching training, applied to leaders carrying real organizational stakes.",
  },
  {
    Icon: Heart,
    label: "Nonprofit & faith-led orgs",
    detail:
      "Built specifically for the realities of purpose-driven cultures and boards.",
  },
];

const services = [
  {
    Icon: Compass,
    label: "Leadership and People Advisory",
    problem: "For consequential decisions where getting it wrong is expensive.",
    body:
      "Confidential, senior counsel for executive directors, CEOs, and boards navigating leadership transitions, sensitive people decisions, and organizational change with real stakes.",
  },
  {
    Icon: ChalkboardTeacher,
    label: "Supervisory Training and Readiness",
    problem: "When managers are inconsistent, undertrained, or quietly exposed.",
    body:
      "Practical, HR-aware development that closes the gap between policy and supervisory judgment — so managers lead with consistency, fairness, and accountability.",
  },
  {
    Icon: Users,
    label: "Executive and Leadership Coaching",
    problem: "When leaders need a trusted outside thinking partner.",
    body:
      "Coaching scoped to real organizational stakes — for executive directors, emerging leaders, and senior teams under pressure.",
  },
  {
    Icon: ShieldCheck,
    label: "Culture, Conduct, and Sensitive Facilitation",
    problem: "When conduct, culture, or trust issues need careful handling.",
    body:
      "Legal-informed, people-centered facilitation that helps leaders address difficult moments without creating fear or further disruption.",
  },
  {
    Icon: Microphone,
    label: "Speaking and Leadership Development",
    problem: "When a message needs to land — and travel.",
    body:
      "Keynotes and learning experiences that bring the same judgment Faith 1st brings to advisory engagements, with a clear path back into your organization.",
  },
];

const problemClusters = [
  {
    title: "Leadership pressure",
    items: ["Transition", "Succession", "Board–executive tension"],
  },
  {
    title: "People risk",
    items: ["Conduct concerns", "HR compliance exposure", "Staff trust erosion"],
  },
  {
    title: "Supervisory inconsistency",
    items: ["Manager gaps", "Performance breakdowns", "Discipline ambiguity"],
  },
  {
    title: "Culture strain",
    items: ["Values–operations drift", "Burnout patterns", "Quiet attrition"],
  },
];

const process = [
  {
    n: "01",
    title: "Listen",
    tag: "Discovery",
    body:
      "We begin with the people closest to the work. What is actually happening, who is affected, and what has already been tried — surfaced in real conversations, not surveys.",
  },
  {
    n: "02",
    title: "Diagnose",
    tag: "Pattern recognition",
    body:
      "We separate symptoms from systems. Where is the strain coming from — leadership, supervision, culture, conduct, or compliance — and what is the true sequencing of the response.",
  },
  {
    n: "03",
    title: "Guide",
    tag: "Direct counsel",
    body:
      "We advise leaders directly through the decisions in front of them. Language, structure, and judgment offered in the moment — so the next step feels deliberate, not improvised.",
  },
  {
    n: "04",
    title: "Equip",
    tag: "Capacity left behind",
    body:
      "We leave behind leaders, supervisors, and teams who are more capable than we found them — through coaching, training, embedded support, and the systems that make it durable.",
  },
];

const insights = [
  {
    label: "Note · Leadership transition",
    title: "What boards underestimate about the months after an ED departure.",
    read: "5 min read",
  },
  {
    label: "Essay · Supervisory readiness",
    title: "Why most management training never reaches the moment that matters.",
    read: "7 min read",
  },
  {
    label: "Field note · Culture",
    title: "Naming culture truth without escalating it: a posture for leaders.",
    read: "4 min read",
  },
];

export default function ConceptA() {
  return (
    <main
      data-testid="concept-a-page"
      className="min-h-screen font-manrope antialiased"
      style={{ background: C.bg, color: C.text }}
    >
      <ConceptSwitcher tone="a" />

      {/* Navigation */}
      <header className="border-b" style={{ borderColor: C.line }}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 h-24 flex items-center justify-between">
          <div className="flex items-end gap-4">
            <BrandMark href="/concept-a" size="lg" testId="nav-wordmark" />
            <span
              className="hidden xl:inline-block font-cormorant italic text-[15px] leading-tight pb-2 border-l pl-4 max-w-[220px]"
              style={{ color: `${C.text}A0`, borderColor: `${C.text}28` }}
            >
              {TAGLINE}
            </span>
          </div>
          <nav className="hidden lg:flex items-center gap-8 xl:gap-9">
            {navLinks.map((l) => (
              <a
                key={l}
                href="#"
                data-testid={`nav-link-${l.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[13px] tracking-tight transition-colors whitespace-nowrap"
                style={{ color: `${C.text}B0` }}
                onMouseEnter={(e) => (e.currentTarget.style.color = C.brand)}
                onMouseLeave={(e) => (e.currentTarget.style.color = `${C.text}B0`)}
              >
                {l}
              </a>
            ))}
          </nav>
          <a
            href="#inquiry"
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] tracking-tight rounded-full transition-colors whitespace-nowrap"
            style={{ background: C.brand, color: C.ivory }}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.inverse)}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.brand)}
          >
            Request a consultation
            <ArrowUpRight size={14} weight="bold" />
          </a>
        </div>
      </header>

      {/* HERO — seated formal portrait leads */}
      <section className="relative">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pt-20 pb-24 lg:pt-32 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 fade-up">
              <p
                data-testid="hero-eyebrow"
                className="text-[11px] uppercase tracking-[0.32em] font-medium mb-10"
                style={{ color: C.rose }}
              >
                Concept 01 · Executive Assurance
              </p>

              <h1
                data-testid="hero-title"
                className="font-cormorant font-light text-5xl sm:text-6xl lg:text-[82px] leading-[1.04] tracking-tight"
                style={{ color: C.text }}
              >
                Faith 1st helps mission-driven organizations lead{" "}
                <em className="italic" style={{ color: C.brand }}>
                  people, culture, and change
                </em>{" "}
                with clarity.
              </h1>

              <p
                data-testid="hero-subcopy"
                className="mt-12 max-w-2xl text-[18px] lg:text-[20px] leading-[1.6]"
                style={{ color: `${C.text}D8` }}
              >
                Faith&nbsp;1st partners with leaders, HR teams, and mission-driven
                organizations facing high-stakes supervisory, culture, conduct,
                and leadership challenges — providing the clarity, structure,
                and outside perspective needed to move forward well.
              </p>

              <div className="mt-12 flex flex-wrap items-center gap-x-7 gap-y-4">
                <a
                  href="#inquiry"
                  data-testid="hero-primary-cta"
                  className="inline-flex items-center gap-2 px-8 py-4 text-[14.5px] tracking-tight rounded-full transition-colors"
                  style={{ background: C.brand, color: C.ivory }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.inverse)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.brand)}
                >
                  Request a consultation
                  <ArrowRight size={16} weight="bold" />
                </a>
                <a
                  href="#services"
                  data-testid="hero-secondary-cta"
                  className="group inline-flex items-center gap-1.5 text-[12.5px] uppercase tracking-[0.18em] transition-colors"
                  style={{ color: `${C.text}88` }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = C.brand)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = `${C.text}88`)}
                >
                  Explore services
                  <ArrowRight
                    size={12}
                    weight="bold"
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>
              </div>
            </div>

            {/* Right column — formal seated portrait (IMG_0986) */}
            <aside className="lg:col-span-5 fade-up">
              <div className="relative">
                <figure
                  className="relative aspect-[4/5] overflow-hidden border"
                  style={{ borderColor: C.line, background: C.surface }}
                >
                  <img
                    src={PHOTOS.seatedFormal}
                    alt="Katrina Bernard — Founder of Faith 1st"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                  />
                  <figcaption
                    className="absolute bottom-0 left-0 right-0 px-4 py-3 text-[10px] uppercase tracking-[0.22em] font-chivo flex items-end justify-between"
                    style={{
                      background: `linear-gradient(to top, ${C.text}CC, transparent)`,
                      color: C.ivory,
                    }}
                  >
                    <span>Katrina Bernard</span>
                    <span>Founder &amp; Principal</span>
                  </figcaption>
                </figure>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* TRUST STRIP — verifiable proof anchors */}
      <section
        data-testid="trust-strip"
        className="border-y"
        style={{ background: C.surface, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-10 lg:py-12">
          <div className="flex items-center gap-3 mb-7">
            <span
              className="inline-block h-px w-10"
              style={{ background: C.brand }}
            />
            <p
              className="text-[11px] uppercase tracking-[0.28em] font-medium"
              style={{ color: C.brand }}
            >
              Why leaders bring Faith&nbsp;1st in
            </p>
          </div>
          <div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px border"
            style={{ background: C.line, borderColor: C.line }}
          >
            {trustMarkers.map(({ Icon, label, detail }, i) => (
              <div
                key={label}
                data-testid={`trust-marker-${i}`}
                className="p-6 lg:p-7 flex flex-col"
                style={{ background: C.surface }}
              >
                <div className="flex items-center justify-between mb-5">
                  <Icon size={26} weight="duotone" style={{ color: C.brand }} />
                  <span
                    className="font-cormorant italic text-[16px]"
                    style={{ color: C.rose }}
                  >
                    0{i + 1}
                  </span>
                </div>
                <h3
                  className="font-cormorant font-medium text-[20px] leading-tight tracking-tight mb-3"
                  style={{ color: C.text }}
                >
                  {label}
                </h3>
                <p
                  className="text-[13.5px] leading-[1.65]"
                  style={{ color: `${C.text}C8` }}
                >
                  {detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem section */}
      <section
        data-testid="problem-section"
        className="border-b"
        style={{ background: C.bg, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <p
                className="text-[11px] uppercase tracking-[0.28em] font-medium mb-5"
                style={{ color: C.rose }}
              >
                When the work gets harder
              </p>
              <h2
                className="font-cormorant font-light text-3xl sm:text-4xl lg:text-[52px] leading-[1.05] tracking-tight"
                style={{ color: C.text }}
              >
                When leadership, supervision, or culture strain begins to{" "}
                <em className="italic" style={{ color: C.brand }}>
                  affect the whole organization
                </em>
                .
              </h2>
            </div>
            <p
              className="lg:col-span-5 text-[16.5px] leading-[1.65]"
              style={{ color: `${C.text}D5` }}
            >
              Organizations rarely seek outside support because everything is
              easy. They reach out when leadership transitions become fragile,
              managers are carrying more than they are prepared for, people
              concerns begin to disrupt trust, or culture challenges start
              affecting the mission itself.
            </p>
          </div>

          <div
            className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px border"
            style={{ background: C.line, borderColor: C.line }}
          >
            {problemClusters.map((p, i) => (
              <article
                key={p.title}
                data-testid={`problem-cluster-${i}`}
                className="p-7 flex flex-col"
                style={{ background: C.bg }}
              >
                <span
                  className="font-cormorant italic text-[22px] mb-4"
                  style={{ color: C.rose }}
                >
                  0{i + 1}
                </span>
                <h3
                  className="font-cormorant font-medium text-[22px] leading-tight tracking-tight mb-4"
                  style={{ color: C.text }}
                >
                  {p.title}
                </h3>
                <ul className="space-y-2 mt-auto">
                  {p.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 border-t pt-2 text-[13.5px]"
                      style={{
                        color: `${C.text}B5`,
                        borderColor: `${C.text}14`,
                      }}
                    >
                      <span style={{ color: C.rose }}>·</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section
        id="services"
        data-testid="services-section"
        style={{ background: C.bg }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 mb-14 lg:mb-16">
            <div className="lg:col-span-6">
              <p
                className="text-[11px] uppercase tracking-[0.28em] font-medium mb-5"
                style={{ color: C.rose }}
              >
                Service pathways
              </p>
              <h2
                className="font-cormorant font-light text-4xl sm:text-5xl lg:text-[56px] leading-[1.04] tracking-tight"
                style={{ color: C.text }}
              >
                Five connected paths — <em className="italic" style={{ color: C.brand }}>not scattered services</em>.
              </h2>
            </div>
            <p
              className="lg:col-span-5 lg:col-start-8 text-[16.5px] leading-[1.65]"
              style={{ color: `${C.text}D5` }}
            >
              Each engagement is scoped to the moment your organization is
              navigating. Most clients enter through advisory, supervisory work,
              or coaching, then extend into facilitation or speaking as the work
              unfolds.
            </p>
          </div>

          <div
            className="grid md:grid-cols-2 gap-px border"
            style={{ background: C.line, borderColor: C.line }}
          >
            {services.map((s, i) => {
              const Icon = s.Icon;
              return (
                <div
                  key={s.label}
                  data-testid={`service-${i}`}
                  className="p-7 lg:p-10 flex flex-col group transition-colors"
                  style={{ background: C.bg }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.surface)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.bg)}
                >
                  <div className="flex items-start justify-between mb-7">
                    <Icon size={34} weight="duotone" style={{ color: C.brand }} />
                    <span
                      className="text-[11px] uppercase tracking-[0.22em] font-medium"
                      style={{ color: `${C.text}88` }}
                    >
                      0{i + 1} / 0{services.length}
                    </span>
                  </div>
                  <h3
                    className="font-cormorant font-medium text-[30px] lg:text-[32px] leading-[1.08] tracking-tight mb-3"
                    style={{ color: C.text }}
                  >
                    {s.label}
                  </h3>
                  <p
                    className="font-cormorant italic text-[14.5px] leading-snug mb-5 max-w-sm"
                    style={{ color: C.rose }}
                  >
                    {s.problem}
                  </p>
                  <p
                    className="text-[15px] leading-[1.7]"
                    style={{ color: `${C.text}CC` }}
                  >
                    {s.body}
                  </p>
                  <a
                    href="#inquiry"
                    className="mt-8 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-medium pb-1.5 border-b w-fit transition-all"
                    style={{ color: C.brand, borderColor: `${C.brand}55` }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = C.brand;
                      e.currentTarget.style.gap = "12px";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = `${C.brand}55`;
                      e.currentTarget.style.gap = "8px";
                    }}
                  >
                    Explore fit
                    <ArrowRight size={14} weight="bold" />
                  </a>
                </div>
              );
            })}
            <div
              className="p-9 flex flex-col justify-center"
              style={{ background: C.inverse, color: C.ivory }}
            >
              <Compass size={32} weight="duotone" className="mb-5" style={{ color: `${C.ivory}D8` }} />
              <h3 className="font-cormorant font-light text-[28px] leading-[1.12] mb-3">
                Not sure which path?
              </h3>
              <p className="text-[14px] leading-[1.65] mb-6" style={{ color: `${C.ivory}CC` }}>
                Most engagements begin with a short conversation. We help you
                determine the right format — or refer you elsewhere if
                Faith&nbsp;1st isn’t the right fit.
              </p>
              <a
                href="#inquiry"
                data-testid="services-cta"
                className="inline-flex items-center gap-2 self-start px-5 py-2.5 text-[12.5px] tracking-tight rounded-full transition-colors"
                style={{ background: C.ivory, color: C.inverse }}
              >
                Start the conversation <ArrowRight size={14} weight="bold" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Process section */}
      <section
        data-testid="process-section"
        className="border-t"
        style={{ background: C.surface, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <p
                className="text-[11px] uppercase tracking-[0.28em] font-medium mb-5"
                style={{ color: C.rose }}
              >
                How we engage
              </p>
              <h2
                className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight"
                style={{ color: C.text }}
              >
                A clear, thoughtful approach from the{" "}
                <em className="italic" style={{ color: C.brand }}>
                  first conversation
                </em>{" "}
                forward.
              </h2>
            </div>
            <p
              className="lg:col-span-5 lg:col-start-8 text-[16.5px] leading-[1.65]"
              style={{ color: `${C.text}D5` }}
            >
              Faith 1st begins by understanding what is happening, who is
              affected, and what kind of support will actually serve the
              organization well. From there, the work is scoped with care so
              leaders can move with clarity, alignment, and confidence.
            </p>
          </div>

          <div
            className="grid md:grid-cols-4 gap-px border"
            style={{ background: C.line, borderColor: C.line }}
          >
            {process.map((p) => (
              <div
                key={p.title}
                data-testid={`process-step-${p.n}`}
                className="p-7 lg:p-9 flex flex-col"
                style={{ background: C.bg }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <span
                    className="font-cormorant italic text-[30px] leading-none"
                    style={{ color: C.brand }}
                  >
                    {p.n}
                  </span>
                  <span
                    className="h-px flex-1"
                    style={{ background: `${C.brand}55` }}
                  />
                </div>
                <p
                  className="text-[10.5px] uppercase tracking-[0.24em] font-medium mb-3"
                  style={{ color: C.rose }}
                >
                  {p.tag}
                </p>
                <h3
                  className="font-cormorant font-medium text-[28px] leading-[1.1] mb-4"
                  style={{ color: C.text }}
                >
                  {p.title}
                </h3>
                <p
                  className="text-[14.5px] leading-[1.7]"
                  style={{ color: `${C.text}CC` }}
                >
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder section — warmer contemplative portrait deeper in scroll */}
      <section
        data-testid="founder-block"
        className="border-t"
        style={{ background: C.bg, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <p
                className="text-[11px] uppercase tracking-[0.28em] font-medium mb-5"
                style={{ color: C.rose }}
              >
                Founder · Principal
              </p>
              <h2
                data-testid="founder-title"
                className="font-cormorant font-light text-3xl sm:text-4xl lg:text-[52px] leading-[1.08] tracking-tight"
                style={{ color: C.text }}
              >
                Leadership support grounded in{" "}
                <em className="italic" style={{ color: C.brand }}>
                  judgment, care, and organizational reality
                </em>
                .
              </h2>
              <p
                className="mt-8 max-w-xl text-[17px] leading-[1.65]"
                style={{ color: `${C.text}D5` }}
              >
                Faith&nbsp;1st is led by Katrina Bernard, whose work sits at the
                intersection of people, leadership, culture, and trust. Her
                approach helps organizations address difficult realities with
                both strategic discipline and human wisdom.
              </p>

              <div className="mt-12 grid sm:grid-cols-2 gap-px border max-w-xl"
                style={{ background: C.line, borderColor: C.line }}
              >
                {[
                  {
                    Icon: Scales,
                    title: "Employment-law fluency",
                    body: "Trained to spot legal exposure before it escalates.",
                  },
                  {
                    Icon: Buildings,
                    title: "Senior HR practice",
                    body: "Operating-level HR judgment, not theory.",
                  },
                  {
                    Icon: Users,
                    title: "Executive coaching",
                    body: "Certified, applied to consequential decisions.",
                  },
                  {
                    Icon: Heart,
                    title: "Mission-led orgs",
                    body: "Built for nonprofit and faith-led realities.",
                  },
                ].map(({ Icon, title, body }) => (
                  <div
                    key={title}
                    className="p-5 lg:p-6 flex flex-col"
                    style={{ background: C.bg }}
                  >
                    <Icon
                      size={22}
                      weight="duotone"
                      style={{ color: C.brand }}
                      className="mb-3"
                    />
                    <h4
                      className="font-cormorant font-medium text-[19px] leading-tight tracking-tight mb-1.5"
                      style={{ color: C.text }}
                    >
                      {title}
                    </h4>
                    <p
                      className="text-[13px] leading-[1.6]"
                      style={{ color: `${C.text}B5` }}
                    >
                      {body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <figure
                className="relative aspect-[4/5] overflow-hidden border"
                style={{ borderColor: C.line, background: C.surface }}
              >
                <img
                  src={PHOTOS.contemplative}
                  alt="Katrina Bernard — warm, contemplative portrait"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <figcaption
                  className="absolute bottom-0 left-0 right-0 px-4 py-3 text-[10px] uppercase tracking-[0.22em] font-chivo flex items-end justify-between"
                  style={{
                    background: `linear-gradient(to top, ${C.text}CC, transparent)`,
                    color: C.ivory,
                  }}
                >
                  <span>Katrina Bernard</span>
                  <span>K.B.</span>
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* Insights preview */}
      <section
        data-testid="insights-section"
        className="border-t"
        style={{ background: C.surface, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <p
                className="text-[11px] uppercase tracking-[0.28em] font-medium mb-5"
                style={{ color: C.rose }}
              >
                Insights · Current thinking
              </p>
              <h2
                className="font-cormorant font-light text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight"
                style={{ color: C.text }}
              >
                Notes from the work.
              </h2>
            </div>
            <a
              href="#"
              className="group inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-medium pb-1.5 border-b transition-all"
              style={{ color: C.brand, borderColor: C.brand }}
            >
              See all insights
              <ArrowUpRight
                size={14}
                weight="bold"
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </a>
          </div>

          <div
            className="grid md:grid-cols-3 gap-px border"
            style={{ background: C.line, borderColor: C.line }}
          >
            {insights.map((post, i) => (
              <article
                key={i}
                data-testid={`insight-${i}`}
                className="p-7 lg:p-8 flex flex-col group transition-colors"
                style={{ background: C.bg }}
                onMouseEnter={(e) => (e.currentTarget.style.background = C.surface)}
                onMouseLeave={(e) => (e.currentTarget.style.background = C.bg)}
              >
                <div className="flex items-center gap-2 mb-6">
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{ background: C.brand }}
                  />
                  <p
                    className="text-[10.5px] uppercase tracking-[0.24em] font-medium"
                    style={{ color: C.brand }}
                  >
                    {post.label}
                  </p>
                </div>
                <h3
                  className="font-cormorant font-medium text-[24px] lg:text-[26px] leading-[1.15] tracking-tight mb-8 flex-1"
                  style={{ color: C.text }}
                >
                  {post.title}
                </h3>
                <div
                  className="flex items-center justify-between pt-5 border-t"
                  style={{ borderColor: `${C.text}1F` }}
                >
                  <span
                    className="text-[11.5px] uppercase tracking-[0.2em]"
                    style={{ color: `${C.text}A5` }}
                  >
                    {post.read}
                  </span>
                  <span
                    className="inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.2em] font-medium group-hover:gap-2.5 transition-all"
                    style={{ color: C.brand }}
                  >
                    Read
                    <ArrowRight size={13} weight="bold" />
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        id="inquiry"
        data-testid="inquiry-cta"
        style={{ background: C.inverse, color: C.ivory }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <p
                className="text-[11px] uppercase tracking-[0.28em] font-medium mb-6"
                style={{ color: C.rose }}
              >
                Consultation
              </p>
              <h2
                className="font-cormorant font-light text-5xl sm:text-6xl lg:text-[80px] leading-[1.02] tracking-tight"
                style={{ color: C.ivory }}
              >
                Tell us what your organization is{" "}
                <em className="italic" style={{ color: C.rose }}>
                  navigating
                </em>
                .
              </h2>
              <p
                className="mt-10 max-w-xl text-[18px] lg:text-[19px] leading-[1.6]"
                style={{ color: `${C.ivory}D0` }}
              >
                Share enough context for us to understand the moment. We’ll
                determine the right next step together — whether that’s a short
                conversation, a scoped engagement, or a thoughtful referral
                elsewhere.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <a
                href="#"
                data-testid="inquiry-primary-cta"
                className="group inline-flex items-center gap-3 px-7 py-4 text-[14px] tracking-tight rounded-full transition-colors"
                style={{ background: C.ivory, color: C.inverse }}
              >
                Request a consultation
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: C.surface, color: C.text }}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <BrandMark size="lg" />
              <p
                className="text-[11.5px] uppercase tracking-[0.24em] mt-4 font-medium"
                style={{ color: C.brand }}
              >
                {TAGLINE}
              </p>
              <p
                className="text-[14.5px] leading-[1.7] max-w-xs mt-6"
                style={{ color: `${C.text}D0` }}
              >
                Advisory practice for mission-driven organizations navigating
                high-stakes people and leadership moments.
              </p>
            </div>
            <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-8">
              {[
                ["Practice", ["Advisory", "Supervisory Readiness", "Coaching", "Facilitation", "Speaking"]],
                ["About", ["Approach", "Who we help", "Insights"]],
                ["Connect", ["Consultation", "LinkedIn"]],
              ].map(([title, items]) => (
                <div key={title} className="space-y-3">
                  <div
                    className="text-[10.5px] uppercase tracking-[0.26em] font-medium mb-4 pb-3 border-b"
                    style={{ color: C.brand, borderColor: `${C.text}1F` }}
                  >
                    {title}
                  </div>
                  {items.map((i) => (
                    <a
                      key={i}
                      href="#"
                      className="block text-[14px] transition-colors"
                      style={{ color: `${C.text}E0` }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = C.brand)}
                      onMouseLeave={(e) => (e.currentTarget.style.color = `${C.text}E0`)}
                    >
                      {i}
                    </a>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div
            className="mt-14 pt-6 border-t flex flex-wrap items-center justify-between gap-3 text-[11px] uppercase tracking-[0.24em] font-medium"
            style={{ borderColor: C.line, color: `${C.text}B0` }}
          >
            <span>© {new Date().getFullYear()} Faith 1st · Concept 01</span>
            <span style={{ color: C.brand }}>Executive Assurance</span>
          </div>
        </div>
      </footer>
    </main>
  );
}
